import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/Signup.css"
import TextField from "../components/textfield/TextField";
import SelectField from "../components/selectfield/SelectField";
import { useAuth } from "../context/AuthContext";
import FilePicker from "../components/filepicker/FilePicker";
import Dialog, { DialogProps } from "../components/dialog/Dialog";

interface LocationState {
    mobile: string;
    token: string;
}

interface OperationArea {
    operationalServiceId: string;
    operationServiceName: string;
    serviceTypes: ServiceType[];
}

interface ServiceType {
    id: string;
    serviceName: string;
    mediumTypes: MediumType[];
}

interface MediumType {
    mediumId: string;
    mediumType: string;
}

interface Metadata {
    driver: {
        driverName: string;
        driverContact: string;
        altDriverContact?: string;
        driverUpiId: string;
    };
    driverService: {
        serviceId: string;
        serviceAreaId: string
    };
    cab: {
        cabNumber: string;
        mediumId: string;
    };
    token: string;
}

interface ValidationRule {
    regex?: RegExp;
    required?: boolean;
    custom?: (value: any) => boolean;
    errorMsg: string;
}

const validationRules: Record<string, ValidationRule> = {
    "driver.driverName": { required: true, errorMsg: "Driver Name is required" },
    "driver.driverContact": { regex: /^[6-9]\d{9}$/, required: false, errorMsg: "Driver Contact is invalid" },
    "driver.altDriverContact": { regex: /^[6-9]\d{9}$/, required: false, errorMsg: "Alternate Contact is invalid" },
    "driver.drivingLicense": { required: false, errorMsg: "Driving License is required" },
    "driver.driverUpiId": { required: true, errorMsg: "Driver UPI ID is required" },

    "driverService.serviceId": { required: true, errorMsg: "Service ID is required" },
    "driverService.serviceAreaId": { required: true, errorMsg: "Service Area ID is invalid" },

    "cab.cabNumber": { required: true, errorMsg: "Cab Number is required" },
    "cab.mediumId": { required: true, errorMsg: "Medium ID is required" },

    "files.verificationFile": { required: true, errorMsg: "Driver verification file is required" },
    "files.vehicleFile": { required: true, errorMsg: "Vehicle registration file is required" },
    "files.passportFile": { required: true, errorMsg: "Passport photo is required" },

    "token": { required: true, errorMsg: "Token not found." },
};


const getNestedValue = (obj: any, path: string) => {
    return path.split(".").reduce((acc, key) => (acc ? acc[key] : undefined), obj);
};

const validateMetadata = (metadata: Metadata, files: Record<string, File | null>): Record<string, boolean> => {
    const fieldErrors: Record<string, boolean> = {};

    Object.entries(validationRules).forEach(([path, rule]) => {
        let value: any;

        if (path.startsWith("files.")) {
            const key = path.replace("files.", "");
            value = files[key];
        } else {
            value = getNestedValue(metadata, path);
        }

        if (rule.required && (value === undefined || value === null || value === "")) {
            fieldErrors[path] = true;
            return;
        }

        if (rule.regex && value && typeof value === "string" && !rule.regex.test(value)) {
            fieldErrors[path] = true;
        }

        if (rule.custom && !rule.custom(value)) {
            fieldErrors[path] = true;
        }
    });

    return fieldErrors;
};

const Signup: React.FC = () => {
    const [metadata, setMetadata] = useState<Metadata>({
        driver: {
            driverName: "",
            driverContact: "",
            altDriverContact: "",
            driverUpiId: ""
        },
        driverService: {
            serviceId: "",
            serviceAreaId: ""

        },
        cab: {
            cabNumber: "",
            mediumId: "",
        },
        token: "",
    });

    const [operationAreas, setOperationAreas] = useState<OperationArea[]>([]);
    const [files, setFiles] = useState({
        verificationFile: null as File | null,
        vehicleFile: null as File | null,
        passportFile: null as File | null
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [alert, setAlert] = useState<DialogProps | null>(null);

    const navigate = useNavigate();
    const { setToken } = useAuth();
    const location = useLocation();
    const state = location.state as LocationState;

    const selectedArea = operationAreas.find(
        (area) => area.operationalServiceId == metadata.driverService.serviceAreaId
    );
    const serviceTypes = selectedArea?.serviceTypes || [];

    const selectedService = serviceTypes.find(
        (s) => s.id == metadata.driverService.serviceId
    );
    const mediumTypes = selectedService?.mediumTypes || [];


    useEffect(() => {
        const fetchOperationArea = async () => {
            try {
                const res = await axios.get("https://uragate.in/websites/springapi/driver-registration/v1/public/service-relation");
                setOperationAreas(res.data);
                console.log(res.data)
            } catch (error) {
                console.error(error);
                setAlert({
                    type: "normal",
                    title: "Page load incomplete",
                    message: "Failed to load full page. Load again.",
                    btnName: "Okay",
                    onClick() {
                        window.location.reload()
                    },
                });

            }
        };
        fetchOperationArea();
        setMetadata((prev) => ({ ...prev, token: state.token }));
    }, []);

    useEffect(() => {
        console.log(metadata.driverService)
    }, [metadata.driverService])

    const handleSubmit2 = async () => {
        navigate("/upload")
    }

    const handleSubmit = async () => {
        const fieldErrors = validateMetadata(metadata, files);
        setErrors(fieldErrors);
        console.log(fieldErrors);
        if (Object.keys(fieldErrors).length > 0) return;
        if (!files.verificationFile || !files.vehicleFile || !files.passportFile) {
            setAlert({
                type: "error",
                title: "Error",
                message: "Please upload files",
                btnName: "Okay",
                onClick() {
                    setAlert(null)
                },
            });
            return;
        }

        const formData = new FormData();
        formData.append("verificationFile", files.verificationFile);
        formData.append("vehicleFile", files.vehicleFile);
        formData.append("passportFile", files.passportFile);
        formData.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));

        try {
            setLoading(true);
            const res = await axios.post("https://uragate.in/websites/springapi/driver-registration/v1/auth/registration", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const token = res.data;
            localStorage.setItem("token", token);
            setAlert({
                type: "success",
                title: "Success",
                message: "Signup successful.",
                btnName: "Okay",
                onClick() {
                    setToken(token);
                    navigate("/upload", { replace: true });
                },
            });
        } catch (error: any) {
            console.error(error);
            setAlert({
                type: "error",
                title: "Error",
                message: error.response?.data?.message || "Signup failed.",
                btnName: "Okay",
                onClick() {
                    setAlert(null)
                },
            });
            return;
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (name: string, file: File | null) => {
        setFiles((prev) => ({ ...prev, [name]: file }));
        resetError(`files.${name}`)
    }

    const resetError = (name: string) => {
        setErrors((prev) => ({ ...prev, [name]: false }));
    }

    const handleDriverChange = (name: string, value: string) => {
        let newValue = value;
        if (name === "altDriverContact") {
            newValue = value.replace(/\D/g, "");
            newValue = newValue.slice(0, 10);
        }
        setMetadata((prev) => ({
            ...prev,
            driver: {
                ...prev.driver,
                [name]: newValue,
            },
        }));

        resetError(`driver.${name}`);
    };


    const handleCabChange = (name: string, value: string) => {
        setMetadata((prev) => ({ ...prev, cab: { ...prev.cab, [name]: value } }));
        resetError(`cab.${name}`)
    }

    return (
        <div className="signup-page">
            {/* <div className="spacer"></div> */}
            <div className="signup-main-container">
                <h2 className="title-text">Driver Registration</h2>
                <div className="divider"></div>
                <div className="form-container">
                    <div className="form-with-file">
                        <div className="signup-section">
                            <TextField
                                isFullWidth={true}
                                label={"Full Name"}
                                name={"driverName"}
                                placeholder="Enter your full name"
                                value={metadata.driver.driverName}
                                onChange={handleDriverChange}
                                error={!!errors["driver.driverName"]}
                            />
                            <FilePicker
                                name="passportFile"
                                file={files.passportFile}
                                onChange={handleFileChange}
                                caption="Upload Passport photo"
                                error={!!errors["files.passportFile"]}
                            />
                        </div>
                        <div className="signup-section">
                            <TextField
                                isFullWidth={true}
                                label={"Vehicle Registration Number"}
                                name={"cabNumber"}
                                placeholder="NL-XX-XXXX"
                                value={metadata.cab.cabNumber}
                                onChange={handleCabChange}
                                error={!!errors["cab.cabNumber"]}
                            />
                            <FilePicker
                                name="vehicleFile"
                                file={files.vehicleFile}
                                onChange={handleFileChange}
                                caption="Upload vehicle image"
                                error={!!errors["files.vehicleFile"]}
                            />
                        </div>

                        <div className="signup-section">
                            <TextField
                                isFullWidth={true}
                                label={"Mobile number"}
                                disabled={true}
                                name={"driverContact"}
                                placeholder="Primary contact number"
                                value={state.mobile}
                                onChange={(name, value) => { }}
                                error={!!errors["driver.driverContact"]}
                            />
                            <FilePicker
                                name="verificationFile"
                                file={files.verificationFile}
                                onChange={handleFileChange}
                                caption="EPIC (Aadhaar, Pan, Voter)"
                                error={!!errors["files.verificationFile"]} />
                        </div>

                    </div>
                    <div className="form-section-container">
                        <TextField
                            label={"Alternative number (Optional)"}
                            name={"altDriverContact"}
                            placeholder="Recommended WhatsApp number"
                            value={metadata.driver.altDriverContact}
                            onChange={handleDriverChange}
                            error={!!errors["driver.altDriverContact"]}
                        />
                        <TextField
                            label={"Gpay/PhonePe or UPI ID"}
                            name={"driverUpiId"}
                            placeholder="Enter Gpay/Phone number or UPI ID"
                            value={metadata.driver.driverUpiId}
                            onChange={handleDriverChange}
                            error={!!errors["driver.driverUpiId"]}
                        />
                        <SelectField
                            label="Region of Operation"
                            defaultOption="Select region of operation"
                            name={"serviceAreaId"}
                            value={metadata.driverService.serviceAreaId}
                            options={operationAreas.map((area) => ({
                                value: area.operationalServiceId,
                                label: area.operationServiceName,
                            }))}
                            onChange={(name, value) => {
                                setMetadata((prev) => ({
                                    ...prev,
                                    driverService: { ...prev.driverService, serviceAreaId: value, serviceId: "" },
                                    cab: { ...prev.cab, mediumId: "" }
                                }))
                                resetError("driverService.serviceAreaId")
                            }}
                            error={!!errors["driverService.serviceAreaId"]}
                        />
                        <SelectField
                            disabled={!metadata.driverService.serviceAreaId}
                            label="Service Type"
                            defaultOption="Select service type"
                            name={"serviceId"}
                            value={metadata.driverService.serviceId}
                            options={serviceTypes.map((s) => ({
                                value: s.id,
                                label: s.serviceName,
                            }))}
                            onChange={(name, value) => {
                                setMetadata((prev) => ({
                                    ...prev,
                                    driverService: { ...prev.driverService, serviceId: value },
                                    cab: { ...prev.cab, mediumId: "" }
                                }))
                                resetError("driverService.serviceId")
                            }}
                            error={!!errors["driverService.serviceId"]}
                        />
                        <SelectField
                            disabled={!metadata.driverService.serviceId}
                            label="Vehicle category"
                            defaultOption="Select vehicle category"
                            name={"mediumId"}
                            value={metadata.cab.mediumId}
                            options={mediumTypes.map((s) => ({
                                value: s.mediumId,
                                label: s.mediumType,
                            }))}
                            onChange={handleCabChange}
                            error={!!errors["cab.mediumId"]}
                        />
                        <button className="next-btn" onClick={handleSubmit} disabled={loading}>{loading ? "Processsing.." : "Next"}</button>
                    </div>
                </div>
            </div>
            {/* <div className="spacer"></div> */}
            {alert &&
                <Dialog type={alert.type} title={alert.title} message={alert.message} btnName={alert.btnName} onClick={alert.onClick} />}
        </div>
    );
};

export default Signup;
